import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Linkedin, Send, CheckCircle, Copy } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import PortfolioAPI from '../services/api';

const Contact = () => {
  const { toast } = useToast();
  const [portfolioData, setPortfolioData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load portfolio data
  useEffect(() => {
    const fetchPortfolio = async () => {
      try {
        const data = await PortfolioAPI.getPortfolio();
        setPortfolioData(data);
      } catch (error) {
        console.error('Error loading portfolio:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolio();
  }, []);

  // Function to copy email to clipboard
  const copyEmail = async () => {
    if (!portfolioData) return;
    const email = portfolioData.personalInfo.email;
    try {
      await navigator.clipboard.writeText(email);
      toast({
        title: "Email Copiado",
        description: `${email} ha sido copiado al portapapeles`,
      });
    } catch (err) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = email;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast({
        title: "Email Copiado",
        description: `${email} ha sido copiado al portapapeles`,
      });
    }
  };

  // Function to copy phone to clipboard
  const copyPhone = async () => {
    if (!portfolioData) return;
    const phone = portfolioData.personalInfo.phone;
    try {
      await navigator.clipboard.writeText(phone);
      toast({
        title: "Teléfono Copiado",
        description: `${phone} ha sido copiado al portapapeles`,
      });
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = phone;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast({
        title: "Teléfono Copiado",
        description: `${phone} ha sido copiado al portapapeles`,
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await PortfolioAPI.sendContactMessage(formData);
      
      if (response.success) {
        toast({
          title: "Mensaje Enviado",
          description: response.message,
        });
        setFormData({ name: '', email: '', company: '', message: '' });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Error",
        description: "No se pudo enviar el mensaje. Por favor, intenta de nuevo.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading || !portfolioData) {
    return (
      <section id="contact" className="py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-600 mx-auto"></div>
          <p className="mt-4 text-stone-600">Cargando información de contacto...</p>
        </div>
      </section>
    );
  }

  const contactInfo = [
    {
      icon: Mail,
      label: "Email",
      value: portfolioData.personalInfo.email,
      action: copyEmail,
      copyable: true
    },
    {
      icon: Phone,
      label: "Teléfono",
      value: portfolioData.personalInfo.phone,
      action: copyPhone,
      copyable: true
    },
    {
      icon: MapPin,
      label: "Ubicación",
      value: portfolioData.personalInfo.location,
      action: null,
      copyable: false
    },
    {
      icon: Linkedin,
      label: "LinkedIn",
      value: "Conectar en LinkedIn",
      action: () => window.open(portfolioData.personalInfo.linkedin, '_blank'),
      copyable: false
    }
  ];

  return (
    <section id="contact" className="py-24 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-stone-900 mb-6">
            Hablemos de Tu Proyecto
          </h2>
          <p className="text-xl text-stone-600 max-w-3xl mx-auto">
            ¿Tienes un desafío agronómico que requiere innovación y experiencia? 
            Conversemos sobre cómo puedo ayudarte a transformar tu operación.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Information */}
          <div className="space-y-8">
            <Card className="p-8 bg-white border-stone-200 shadow-lg">
              <h3 className="text-2xl font-bold text-stone-900 mb-6">
                Información de Contacto
              </h3>
              <div className="space-y-6">
                {contactInfo.map((item, index) => {
                  const IconComponent = item.icon;
                  return (
                    <div key={index} className="flex items-start gap-4">
                      <div className="p-3 bg-yellow-100 rounded-xl">
                        <IconComponent size={20} className="text-yellow-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-stone-900 mb-1">
                          {item.label}
                        </h4>
                        <div className="flex items-center gap-2">
                          {item.action ? (
                            <button
                              onClick={item.action}
                              className="text-stone-600 hover:text-yellow-600 transition-colors text-left"
                            >
                              {item.value}
                            </button>
                          ) : (
                            <p className="text-stone-600">{item.value}</p>
                          )}
                          {item.copyable && (
                            <button
                              onClick={item.action}
                              className="ml-2 p-1 hover:bg-yellow-100 rounded transition-colors"
                              title={`Copiar ${item.label.toLowerCase()}`}
                            >
                              <Copy size={14} className="text-yellow-600" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Expertise Areas */}
            <Card className="p-8 bg-olive-600 text-white">
              <h3 className="text-2xl font-bold mb-6">
                Áreas de Consultoría
              </h3>
              <div className="space-y-4">
                {[
                  "Implementación de agricultura de precisión",
                  "Transformación digital en agronegocios",
                  "Desarrollo de equipos agronómicos",
                  "Optimización de cadenas de suministro",
                  "Cultivo de tejidos vegetales",
                  "Certificaciones y mejora continua"
                ].map((area, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle size={16} className="text-yellow-400 flex-shrink-0" />
                    <span>{area}</span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Social Media Update Link */}
            <Card className="p-6 bg-yellow-600 text-stone-900">
              <h4 className="font-bold mb-2">Actualizar Redes Sociales</h4>
              <p className="text-sm mb-4">
                Mantén actualizados los enlaces a tus perfiles profesionales
              </p>
              <Button 
                variant="secondary"
                size="sm"
                className="bg-white text-yellow-600 hover:bg-stone-100"
              >
                Configurar Enlaces
              </Button>
            </Card>
          </div>

          {/* Contact Form */}
          <Card className="p-8 bg-white border-stone-200 shadow-lg">
            <h3 className="text-2xl font-bold text-stone-900 mb-6">
              Enviar Mensaje
            </h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-stone-700">
                    Nombre Completo
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="border-stone-300 focus:border-yellow-600"
                    placeholder="Tu nombre"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-stone-700">
                    Email
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="border-stone-300 focus:border-yellow-600"
                    placeholder="tu@email.com"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="company" className="text-stone-700">
                  Empresa / Organización
                </Label>
                <Input
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="border-stone-300 focus:border-yellow-600"
                  placeholder="Nombre de tu empresa"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-stone-700">
                  Mensaje
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  className="border-stone-300 focus:border-yellow-600 resize-none"
                  placeholder="Cuéntame sobre tu proyecto o desafío agronómico..."
                />
              </div>

              <Button 
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-yellow-600 hover:bg-yellow-500 text-stone-900 font-semibold py-3"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-stone-900 border-t-transparent mr-2"></div>
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send size={16} className="mr-2" />
                    Enviar Mensaje
                  </>
                )}
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Contact;